import {
  type BranchKey,
  configurationKeys,
  invalidateWorkspaceCache,
  useDeleteConciseWorkspace,
} from '@festina-life/api'
import { errorMessageToastContent } from '@festina-life/components'
import { notEmpty, type Optional } from '@festina-life/core'
import { Alert, toast } from '@festina-life/ui/feedback'
import { Checkbox, TextInput } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { ConfirmationBox, Stack } from '@festina-life/ui/structure'
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import { useBaseBranchWorkspaces } from '../../../../hooks/useBaseBranchWorkspaces'
import { isEqualBranch } from '../../../../uiModels/branches/helpers'
import { useWorkspaceManagerContext } from '../../../../workspace/workspaceManagerContext'
import { ConfigurationBranchStatus } from '../../../branchNameWithStatus/ConfigurationBranchStatus'
import { OperationsLoadedButton } from '../../../operationsButton/OperationsLoadedButton'
import { WorkspaceName } from '../../../workspaceName/WorkspaceName'

type WorkspaceError = { workspaceName: string; error: Error }

type Props = {
  branchKey: BranchKey
  label: string
  confirmationTitle: string
  confirmationDescription: string
  isNameConfirmationRequired?: boolean
  onDelete: () => Promise<void>
  isLoading: boolean
  disabled?: boolean
  blockingBranchNames?: string[]
}

export const DeleteBranchConfirmationButton = ({
  branchKey,
  label,
  confirmationTitle,
  confirmationDescription,
  isNameConfirmationRequired,
  onDelete,
  isLoading,
  disabled,
  blockingBranchNames,
}: Props) => {
  const translate = useTranslate()
  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [nameInput, setNameInput] = useState('')
  const [deleteError, setDeleteError] = useState<Optional<Error>>(null)
  const [deleteWorkspacesChecked, setDeleteWorkspacesChecked] = useState(false)
  const [isDeletingWorkspaces, setIsDeletingWorkspaces] = useState(false)
  const [workspaceErrors, setWorkspaceErrors] = useState<WorkspaceError[]>([])
  const queryClient = useQueryClient()
  const workspaces = useBaseBranchWorkspaces(branchKey)
  const { mutateAsync: deleteWorkspace } = useDeleteConciseWorkspace()
  const didDeleteWorkspaceRef = useRef(false)
  const branchName = branchKey.name

  const namedWorkspaces = workspaces.map(({ name }) => name).filter(notEmpty)

  const closeConfirmation = async () => {
    setNameInput('')
    setIsConfirmationOpen(false)
    setDeleteError(null)
    setDeleteWorkspacesChecked(false)
    setWorkspaceErrors([])

    if (didDeleteWorkspaceRef.current) {
      await Promise.allSettled(
        namedWorkspaces.map((name) =>
          invalidateWorkspaceCache({
            queryClient,
            name,
            server: branchKey.server,
          }),
        ),
      )
    }
    didDeleteWorkspaceRef.current = false
  }

  const handleDelete = async () => {
    // Workspaces are deleted before the branch so that errors remain visible.
    // Deleting the branch first would unmount this panel, hiding any workspace errors.
    if (deleteWorkspacesChecked && workspaces.length > 0) {
      setIsDeletingWorkspaces(true)

      const results = await Promise.allSettled(
        namedWorkspaces.map((name) =>
          deleteWorkspace({
            name,
            server: branchKey.server,
            shouldSkipCacheInvalidation: true,
          }),
        ),
      )

      didDeleteWorkspaceRef.current = true

      setIsDeletingWorkspaces(false)

      const errors = results
        .map((result, index) => ({
          result,
          workspace: namedWorkspaces[index],
        }))
        .filter(({ result }) => result.status === 'rejected')
        .map(({ workspace, result }) => ({
          workspaceName: workspace,
          error: (result as PromiseRejectedResult).reason,
        }))

      if (errors.length) {
        setWorkspaceErrors(errors)
        return
      }
    }

    try {
      await onDelete()

      if (
        currentWorkspaceKey &&
        isEqualBranch(branchKey, currentWorkspaceKey.branchKey)
      ) {
        await queryClient.invalidateQueries({
          queryKey: configurationKeys.fetchWorkspaceAllErrorsKeyPrefix({
            server: currentWorkspaceKey.branchKey.server,
            workspaceName: currentWorkspaceKey.name,
          }),
        })
      }

      toast({
        variant: 'success',
        title: translate(
          'branchDetailsPanel.tabs.manage.deleteBranch.toast.success',
          { branchName },
        ),
      })

      void closeConfirmation()
    } catch (error) {
      setDeleteError(error)
    }
  }

  const isNameConfirmed =
    !isNameConfirmationRequired || nameInput === branchName
  const isBlocked = !!blockingBranchNames?.length
  const hasWorkspaceErrors = !!workspaceErrors.length

  return (
    <>
      <ConfirmationBox
        isOpen={isConfirmationOpen}
        onCancel={closeConfirmation}
        title={confirmationTitle}
        onAccept={handleDelete}
        confirmLabel={translate('delete')}
        isDanger
        panelClassName="max-w-lg"
        isLoading={isLoading || isDeletingWorkspaces}
        isButtonDisabled={isBlocked || !isNameConfirmed || hasWorkspaceErrors}
      >
        <Stack className="flex-col" gap="spacious">
          {!!deleteError && (
            <Alert
              severity="error"
              title={
                errorMessageToastContent({
                  error: deleteError,
                  translate,
                }).title
              }
            />
          )}
          {workspaceErrors.map(({ workspaceName, error }) => (
            <Alert key={workspaceName} severity="error" title={workspaceName}>
              {errorMessageToastContent({ error, translate }).title}
            </Alert>
          ))}
          {isBlocked ? (
            <>
              <Alert
                severity="warning"
                title={translate(
                  'branchDetailsPanel.tabs.manage.deleteBranch.blockingBranches',
                )}
              />
              <Stack gap="spacious" className="flex-wrap">
                {blockingBranchNames!.map((name) => (
                  <ConfigurationBranchStatus
                    branchKey={{ name, server: branchKey.server }}
                    key={name}
                  />
                ))}
              </Stack>
              <p>
                {translate(
                  'branchDetailsPanel.tabs.manage.deleteBranch.suggestions',
                )}
              </p>
            </>
          ) : (
            <>
              <p>{confirmationDescription}</p>
              {isNameConfirmationRequired && (
                <TextInput
                  wrapperProps={{
                    label: translate(
                      'branchDetailsPanel.tabs.manage.deleteBranch.nameConfirmation.label',
                      { branchName },
                    ),
                  }}
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                />
              )}
              {namedWorkspaces.length > 0 && (
                <>
                  <Checkbox
                    wrapperProps={{
                      label: translate(
                        'branchDetailsPanel.tabs.manage.workspaces.deleteCheckbox',
                      ),
                    }}
                    checked={deleteWorkspacesChecked}
                    onChange={setDeleteWorkspacesChecked}
                  />
                  <Stack gap="spacious" className="flex-wrap">
                    {namedWorkspaces.map((name) => (
                      <WorkspaceName
                        workspaceKey={{ name, branchKey }}
                        isShowingIcon={false}
                        key={name}
                      />
                    ))}
                  </Stack>
                </>
              )}
            </>
          )}
        </Stack>
      </ConfirmationBox>
      <OperationsLoadedButton
        startIcon="Trash"
        isDanger
        onClick={() => setIsConfirmationOpen(true)}
        disabled={disabled}
      >
        {label}
      </OperationsLoadedButton>
    </>
  )
}
